import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

function MutedSpeakerIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('size-5 shrink-0', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M11 5 6 9H4v6h2l5 4V5Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="m17 7-10 10M7 7l10 10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  )
}

function SpeakerOnIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('size-4 shrink-0', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M11 5 6 9H4v6h2l5 4V5Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        fill="currentColor"
        fillOpacity="0.2"
      />
      <path d="M16 9.5a4 4 0 0 1 0 5M18.5 7a7 7 0 0 1 0 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export type MotionReelCardProps = {
  reelId: string
  src: string
  poster: string
  description: string
  reduced: boolean
  /** False when the parent region (strip or hero collage) has scrolled mostly off-screen */
  stripAudible: boolean
  onAudibleChange: (id: string, audible: boolean) => void
  /**
   * Portrait tile (gallery strip) vs filling a CSS grid cell (home hero).
   * When true, video is `absolute inset-0` so the parent controls height (e.g. row-span).
   */
  fillHeight?: boolean
}

/**
 * Muted autoplay while in view; when focused, sound is on until the visitor toggles with Space, Enter, or a second click.
 * Sound is muted when the reel or the parent region leaves the viewport.
 * With reduced motion, shows poster only (no autoplay).
 */
export function MotionReelCard({
  reelId,
  src,
  poster,
  description,
  reduced,
  stripAudible,
  onAudibleChange,
  fillHeight = false,
}: MotionReelCardProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [focused, setFocused] = useState(false)
  const [visible, setVisible] = useState(false)
  const [userMutedWhileFocused, setUserMutedWhileFocused] = useState(false)
  const suppressToggleClickRef = useRef(false)
  const suppressToggleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSuppressToggleTimer = () => {
    if (suppressToggleTimerRef.current != null) {
      clearTimeout(suppressToggleTimerRef.current)
      suppressToggleTimerRef.current = null
    }
  }

  useEffect(() => {
    if (reduced) return
    const wrap = wrapRef.current
    if (!wrap) return

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0]
        if (!e) return
        setVisible(e.isIntersecting && e.intersectionRatio >= 0.35)
      },
      { threshold: [0, 0.2, 0.35, 0.55, 0.8] }
    )
    io.observe(wrap)
    return () => io.disconnect()
  }, [reduced])

  useEffect(() => {
    if (reduced) return
    const vid = videoRef.current
    if (!vid) {
      onAudibleChange(reelId, false)
      return
    }
    if (focused) {
      const inViewEnough = visible && stripAudible
      vid.muted = !inViewEnough || userMutedWhileFocused
      void vid.play().catch(() => {})
    } else if (visible) {
      vid.muted = true
      void vid.play().catch(() => {})
    } else {
      vid.pause()
    }
    const audible = focused && visible && stripAudible && !userMutedWhileFocused
    onAudibleChange(reelId, audible)
  }, [
    reduced,
    focused,
    visible,
    userMutedWhileFocused,
    stripAudible,
    reelId,
    onAudibleChange,
  ])

  useEffect(
    () => () => {
      if (suppressToggleTimerRef.current != null) {
        clearTimeout(suppressToggleTimerRef.current)
        suppressToggleTimerRef.current = null
      }
      onAudibleChange(reelId, false)
    },
    [reelId, onAudibleChange]
  )

  const onFocus = () => {
    setFocused(true)
    setUserMutedWhileFocused(false)
    wrapRef.current?.scrollIntoView({
      behavior: reduced ? 'instant' : 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }

  const onPointerDown = (e: React.PointerEvent<HTMLVideoElement>) => {
    if (e.button !== 0) return
    const vid = videoRef.current
    if (!vid || document.activeElement === vid) return
    clearSuppressToggleTimer()
    suppressToggleClickRef.current = true
    suppressToggleTimerRef.current = setTimeout(() => {
      suppressToggleClickRef.current = false
      suppressToggleTimerRef.current = null
    }, 450)
  }

  const onBlur = () => {
    clearSuppressToggleTimer()
    suppressToggleClickRef.current = false
    setFocused(false)
    setUserMutedWhileFocused(false)
  }

  const toggleSoundWhileFocused = () => {
    if (!focused) return
    setUserMutedWhileFocused((m) => !m)
  }

  const onVideoKeyDown = (e: React.KeyboardEvent<HTMLVideoElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      toggleSoundWhileFocused()
    }
  }

  const onVideoClick = () => {
    if (suppressToggleClickRef.current) return
    if (document.activeElement === videoRef.current) {
      toggleSoundWhileFocused()
    }
  }

  const soundAudible = focused && visible && stripAudible && !userMutedWhileFocused

  const ariaLabel = `${description} While this clip is focused and on screen, sound plays until you mute it. Sound turns off if you scroll away. Press Space or Enter, or click again, to turn sound off or on.`

  const frameBase =
    'relative w-full overflow-hidden rounded-[var(--radius-button)] border border-hairline bg-ink shadow-sm transition-[transform,box-shadow] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)] motion-reduce:transform-none motion-reduce:hover:translate-y-0'

  if (reduced) {
    return (
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-[var(--radius-button)] border border-hairline bg-canvas shadow-sm',
          fillHeight ? 'h-full min-h-0' : 'aspect-[9/16]'
        )}
      >
        <img
          src={poster}
          alt=""
          width={736}
          height={1308}
          loading="lazy"
          decoding="async"
          className={cn('object-cover', fillHeight ? 'absolute inset-0 h-full w-full' : 'h-full w-full')}
        />
        <span className="sr-only">{description}</span>
      </div>
    )
  }

  return (
    <div
      ref={wrapRef}
      className={cn(frameBase, fillHeight ? 'h-full min-h-0' : 'aspect-[9/16]')}
    >
      <video
        ref={videoRef}
        className={cn(
          'cursor-pointer object-cover outline-none',
          'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          fillHeight ? 'absolute inset-0 h-full w-full' : 'h-full w-full'
        )}
        src={src}
        poster={poster}
        muted
        playsInline
        loop
        preload="metadata"
        tabIndex={0}
        title="Sound plays when this clip is focused and still on screen. It mutes if you scroll away. Press Space or Enter, or click again, to toggle."
        aria-label={ariaLabel}
        onFocus={onFocus}
        onBlur={onBlur}
        onPointerDown={onPointerDown}
        onKeyDown={onVideoKeyDown}
        onClick={onVideoClick}
      />
      <div
        className="pointer-events-none absolute inset-0 flex items-end justify-end p-2.5 sm:p-3"
        aria-hidden
      >
        <div
          className={cn(
            'flex size-9 items-center justify-center rounded-full text-white shadow-sm ring-1 ring-white/20',
            focused && soundAudible ? 'bg-primary/90' : 'bg-black/50 backdrop-blur-[2px]'
          )}
        >
          {focused && soundAudible ? (
            <SpeakerOnIcon className="size-4.5 text-white" />
          ) : (
            <MutedSpeakerIcon className="size-4.5 text-white" />
          )}
        </div>
      </div>
    </div>
  )
}
